/**
 * Tính toán cho khung xem ảnh (ImageDialog). Tách khỏi component để kiểm được bằng unit test
 * (vitest chạy environment 'node', không có DOM).
 *
 * Quy ước THANG ĐO: mức phóng là tỉ lệ so với KÍCH THƯỚC GỐC của ảnh — 100% = cỡ gốc, nhỏ hơn cỡ
 * gốc thì dưới 100%. Ảnh chụp điện thoại lớn hơn khung nhìn nên lúc mở ra thường chỉ vài chục phần
 * trăm; đó là con số đúng, không phải 100%.
 */

/** Trần phóng: 400% cỡ gốc. Trên mức này ảnh chỉ còn là các ô màu. */
export const MAX_SCALE = 4;
export const SCALE_STEP = 1.25;

/** Tỉ lệ chiều rộng/cao của khung nhìn dành cho ảnh (chừa chỗ cho nút đóng + thanh zoom). */
export const VIEWPORT_RATIO = { width: 0.96, height: 0.88 };

export interface Size {
  width: number;
  height: number;
}

/**
 * Tỉ lệ để ảnh vừa khung nhìn, so với cỡ gốc. Luôn ≤ 1: ảnh nhỏ hơn khung thì giữ nguyên cỡ gốc
 * (tỉ lệ 1) chứ không kéo giãn cho mờ — muốn to hơn thì bấm nút phóng.
 */
export const fitScale = (natural: Size, viewport: Size): number => {
  if (natural.width <= 0 || natural.height <= 0) return 1;

  const maxWidth = viewport.width * VIEWPORT_RATIO.width;
  const maxHeight = viewport.height * VIEWPORT_RATIO.height;

  return Math.min(maxWidth / natural.width, maxHeight / natural.height, 1);
};

/**
 * Kẹp mức phóng trong [minScale, MAX_SCALE]. `minScale` là mức vừa khung nhìn — không cho nhỏ hơn
 * vì dưới mức đó ảnh chỉ nhỏ đi vô ích trong khi vẫn còn chỗ trống.
 */
export const clampScale = (scale: number, minScale: number): number =>
  Math.min(Math.max(MAX_SCALE, minScale), Math.max(minScale, scale));

export const scaleIn = (scale: number, minScale: number): number =>
  clampScale(scale * SCALE_STEP, minScale);

export const scaleOut = (scale: number, minScale: number): number =>
  clampScale(scale / SCALE_STEP, minScale);

/** Cỡ hiển thị (px) = cỡ gốc × mức phóng. */
export const displaySize = (natural: Size, scale: number): Size => ({
  width: natural.width * scale,
  height: natural.height * scale,
});

/** Xoay thêm 90° theo chiều kim đồng hồ, kẹp về 0–270. */
export const nextQuarterTurn = (degrees: number): number => {
  const normalized = ((degrees % 360) + 360) % 360;
  return (normalized + 90) % 360;
};

/**
 * Cỡ khung sau khi xoay. 90° và 270° đổi rộng với cao để ảnh vẫn vừa màn hình;
 * 0° và 180° giữ nguyên.
 */
export const orientedSize = (natural: Size, degrees: number): Size => {
  const turn = ((degrees % 360) + 360) % 360;
  if (turn === 90 || turn === 270) {
    return { width: natural.height, height: natural.width };
  }
  return natural;
};

/** Số phần trăm hiện trên thanh zoom: so với cỡ gốc. */
export const scalePercent = (scale: number): number => Math.round(scale * 100);
